import React, { FC } from 'react';
import { camelCase } from 'lodash-es';
import { useIntl } from 'react-intl';
import FormErrorMessage from '../../../../../atoms/FormErrorMessage';
import {
  ErrorType,
  Questionnaire,
  CustomerField,
  Survey,
} from '../../../../../interfaces/merchants/resources/books';

type InnerProps = { name: string };

const BlankMessage: FC<InnerProps> = ({ name }) => {
  const intl = useIntl();
  return (
    <FormErrorMessage>
      {intl.formatMessage({
        id: `books.errors.${name}.blank`,
        defaultMessage: intl.formatMessage({ id: 'books.errors.blank' }),
      })}
    </FormErrorMessage>
  );
};

const TakenMessage: FC<InnerProps> = ({ name }) => {
  const intl = useIntl();
  return (
    <FormErrorMessage>
      {intl.formatMessage({
        id: `books.errors.${name}.taken`,
        defaultMessage: intl.formatMessage({ id: 'books.errors.taken' }),
      })}
    </FormErrorMessage>
  );
};

const InvalidMessage: FC<InnerProps> = ({ name }) => {
  const intl = useIntl();
  return (
    <FormErrorMessage>
      {intl.formatMessage({
        id: `books.errors.${name}.invalid`,
        defaultMessage: intl.formatMessage({
          id: 'books.errors.invalid',
        }),
      })}
    </FormErrorMessage>
  );
};

const InvalidDomainMessage: FC<InnerProps> = ({ name }) => {
  const intl = useIntl();
  return (
    <FormErrorMessage>
      {intl.formatMessage({
        id: `books.errors.${name}.invalidDomain`,
        defaultMessage: intl.formatMessage({
          id: 'books.errors.invalidDomain',
        }),
      })}
    </FormErrorMessage>
  );
};

const ERROR_COMPONENTS: {
  [error in ErrorType]: React.FC<Pick<InnerProps, 'name'>>;
} = {
  [ErrorType.Blank]: BlankMessage,
  [ErrorType.Taken]: TakenMessage,
  [ErrorType.Invalid]: InvalidMessage,
  [ErrorType.InvalidDomain]: InvalidDomainMessage,
  [ErrorType.LoginFailed]: InvalidMessage,
};

const isCustomerField = (f: FieldSource): f is CustomerField => 'name' in f;
const isSurvey = (f: FieldSource): f is Survey => 'question' in f;
const isQuestionnaire = (f: FieldSource): f is Questionnaire => 'type' in f;

const toName = (fieldSource: FieldSource): string => {
  if (isCustomerField(fieldSource)) {
    return camelCase(fieldSource.name);
  }
  if (isSurvey(fieldSource)) {
    return 'survey';
  }
  if (isQuestionnaire(fieldSource)) {
    return fieldSource.type;
  }
  return '';
};

type FieldSource = CustomerField | Survey | Questionnaire;
interface Props {
  fieldSource: FieldSource;
}

const InputUserInfoErrorMessage: FC<Props> = (props) => {
  const { fieldSource } = props;

  if (!fieldSource.errors) {
    return null;
  }
  if (fieldSource.errors.length === 0) {
    return null;
  }

  return (
    <>
      {fieldSource.errors.map((error, index) => {
        const Component = ERROR_COMPONENTS[error];
        const name = toName(fieldSource);
        return <Component key={index} name={name} />;
      })}
    </>
  );
};

export default InputUserInfoErrorMessage;
